import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { auth } from "../api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedBy, setImpersonatedBy] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("eq_user");
    const token  = localStorage.getItem("eq_access_token");
    if (stored && token) {
      try { setUser(JSON.parse(stored)); }
      catch { clearSession(); }
    }
    const storedImpersonator = localStorage.getItem("eq_impersonated_by");
    if (storedImpersonator) {
      try { setImpersonatedBy(JSON.parse(storedImpersonator)); } catch { /* ignore */ }
    }
    setLoading(false);
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem("eq_access_token");
    localStorage.removeItem("eq_refresh_token");
    localStorage.removeItem("eq_user");
    localStorage.removeItem("eq_impersonated_by");
    localStorage.removeItem("eq_original_session");
    setUser(null);
    setImpersonatedBy(null);
  }, []);

  const applyTokenData = useCallback((data) => {
    localStorage.setItem("eq_access_token",  data.access);
    localStorage.setItem("eq_refresh_token", data.refresh);

    const userData = {
      id:                 data.user_id,
      email:              data.email,
      name:               data.name,
      role:               data.role,
      facilityId:         data.facility_id,
      facilityName:       data.facility_name,
      organisationId:     data.organisation_id,     // NEW — Phase B
      organisationName:   data.organisation_name,   // NEW — Phase B
      mustChangePassword: data.must_change_password,
    };

    localStorage.setItem("eq_user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await auth.login({ email, password });
    return applyTokenData(data);
  }, [applyTokenData]);

  // Elyon HMS embed integration — exchanges a one-time sso_url code for a
  // real session, identically shaped to a normal login (see /sso/:code).
  const loginWithSsoCode = useCallback(async (code) => {
    const { data } = await auth.ssoExchange(code);
    return applyTokenData(data);
  }, [applyTokenData]);

  // Super admin "log in as" a user. Stashes the current (real) session so
  // stopImpersonating() can restore it without a second login.
  const impersonate = useCallback(async (userId) => {
    const originalSession = {
      access:  localStorage.getItem("eq_access_token"),
      refresh: localStorage.getItem("eq_refresh_token"),
      user:    localStorage.getItem("eq_user"),
    };

    const { data } = await auth.impersonate(userId);
    const userData = applyTokenData(data);

    localStorage.setItem("eq_original_session", JSON.stringify(originalSession));
    localStorage.setItem("eq_impersonated_by", JSON.stringify(data.impersonated_by));
    setImpersonatedBy(data.impersonated_by);

    return userData;
  }, [applyTokenData]);

  const stopImpersonating = useCallback(() => {
    const raw = localStorage.getItem("eq_original_session");
    if (!raw) { clearSession(); return; }

    try {
      const original = JSON.parse(raw);
      localStorage.setItem("eq_access_token",  original.access);
      localStorage.setItem("eq_refresh_token", original.refresh);
      localStorage.setItem("eq_user",          original.user);
      localStorage.removeItem("eq_original_session");
      localStorage.removeItem("eq_impersonated_by");
      setUser(JSON.parse(original.user));
      setImpersonatedBy(null);
    } catch {
      clearSession();
    }
  }, [clearSession]);

  const logout = useCallback(() => { clearSession(); }, [clearSession]);

  const clearMustChange = useCallback(() => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, mustChangePassword: false };
      localStorage.setItem("eq_user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, login, loginWithSsoCode, logout, clearMustChange,
      impersonate, stopImpersonating, impersonatedBy,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
